import { useEffect, useMemo, useState } from 'react';

import { useInitLikedProducts } from '@widgets/like-product';
import { UxGoodsCardItemForVerticalProductCard } from '@common/product-card';
import { useCatalogProductIdParams, useDocumentVisibleOnce } from '@domains/pdp-common';

import { MIN_DISPLAY_COUNT } from '../constants/constant';
import { GetPdpRecommendIdList_pdp_recommend_id_list_attribute_value } from '../graphql/pdp_recommend_id_list_types.2';
import { usePdpInfiniteRecommendGroupByProductIdList } from '../query/usePdpInfiniteRecommendGroupByProductIdList';
import { usePdpRecommendIdList } from '../query/usePdpRecommendIdList';
import {
  convertPdpGoodsCardItemListToCatalogProductIdList,
  convertUxGoodsCardItemFromPdpGoodsCardItem,
} from '../utils/convert';
import { GetRecommendListType } from './types';

const SELECTED_KEY = 'selected';
const VIEW_COUNT = 20;
export const useInfiniteRecommendGroupByProductIdList = (): GetRecommendListType => {
  const { ref: targetRef, isPermanentlyOnce } = useDocumentVisibleOnce();
  const param = useCatalogProductIdParams();

  const { data, isFetched: isFetchedRecommendIdList } = usePdpRecommendIdList({
    catalog_product_id: param.catalog_product_id,
    enabled: isPermanentlyOnce,
  });

  const selectedDrLogMap = useMemo(() => {
    const idListValueArray = data?.find((o) => o.key === SELECTED_KEY)?.attribute_value ?? [];

    return idListValueArray.reduce<Record<string, string>>((prev, curr) => {
      if (curr && curr.dr_log) {
        prev[curr.id] = curr.dr_log;
      }
      return prev;
    }, {});
  }, [data]);

  const selectedProductIdList = useMemo(() => data?.find((o) => o.key === SELECTED_KEY)?.attribute_value ?? [], [data]);

  const [pageNumber, setPageNumber] = useState(1);
  const [itemList, setItemList] = useState<UxGoodsCardItemForVerticalProductCard[]>([]);
  const [idList, setIdList] = useState<string[]>([]);
  const hasNextPage = selectedProductIdList.length > VIEW_COUNT * pageNumber;

  const response = usePdpInfiniteRecommendGroupByProductIdList({
    id_list: idList,
    group_type: 'selected_item_list',
    enabled: isPermanentlyOnce,
  });

  useInitLikedProducts({
    data: itemList,
    convert: convertPdpGoodsCardItemListToCatalogProductIdList,
  });

  useEffect(() => {
    const paging = () => {
      const startIndex = (pageNumber - 1) * VIEW_COUNT;
      const endIndex = startIndex + VIEW_COUNT;
      setIdList(
        selectedProductIdList
          .slice(startIndex, endIndex)
          .filter((item): item is GetPdpRecommendIdList_pdp_recommend_id_list_attribute_value => item !== null)
          .map((item) => item.id),
      );
    };
    paging();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProductIdList, pageNumber]);

  useEffect(() => {
    const addList = () => {
      const data = response?.data?.item_list?.map(convertUxGoodsCardItemFromPdpGoodsCardItem) ?? [];
      setItemList(itemList.concat(data));
    };
    addList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [response?.data?.item_list]);

  const fetchNextPage = () => {
    setPageNumber(pageNumber + 1);
  };

  return {
    targetRef,
    response: {
      ...response,
      hasNextPage,
      isFetchingNextPage: response.isFetching,
      fetchNextPage,
    },
    title: response.data?.title?.text ?? '셀렉티드 추천 상품',
    subTitle: response.data?.sub_title?.text,
    isAd: response.data?.is_ad,
    itemList: itemList,
    isEmpty:
      (response.isFetched && !hasNextPage && itemList.length < MIN_DISPLAY_COUNT) ||
      (!idList.length && isFetchedRecommendIdList),
    selectedDrLogMap,
  };
};
